import React from "react";
import { useCreateUserMutation } from '../../redux/services/auth/registerAPI';
import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import queryString from 'query-string';
import Swal from 'sweetalert2';

export default function Register() {
  const [createUser] = useCreateUserMutation();
  const navigate = useNavigate();

  const form = useFormik({
    initialValues: { name: "", family: "", mobile_number: "", password: "", repassword: "", email: "" },
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      const response = await createUser(queryString.stringify({
        name: values.name,
        family: values.family,
        mobile_number: values.mobile_number,
        password: values.password,
        email: values.email
      }));

      if (response.data) {
        Swal.fire({ icon: 'success', text: "Active code is sent to your email", timer: 1800, showConfirmButton: false, customClass: { popup: 'sweetalert2-custom-popup' } });
        navigate('/verify');
      } else {
        Swal.fire({ icon: 'warning', text: response.error?.data?.mobile_number || response.error?.data?.message || 'Registration failed.', confirmButtonText: 'OK', customClass: { popup: 'sweetalert2-custom-popup' } });
      }

      resetForm();
      setTimeout(() => setSubmitting(false), 300);
    },
    validate: (values) => {
      const errors = {};
      if (!values.name) errors.name = 'Name is required';
      if (!values.family) errors.family = 'Family name is required';
      if (!values.mobile_number) errors.mobile_number = 'Mobile number is required';
      if (!values.password) errors.password = 'Password is required';
      if (!values.repassword) errors.repassword = 'Please confirm your password';
      if (values.password !== values.repassword) errors.repassword = 'Passwords do not match';
      return errors;
    }
  });

  const fieldClass = (name) => `field ${form.errors[name] && form.touched[name] ? 'border-red-300 ring-4 ring-red-50' : ''}`;

  return (
    <section className="bg-surface py-12 sm:py-16">
      <div className="section-shell grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        <div className="premium-card bg-slate-950 p-8 text-white">
          <span className="inline-flex rounded-full bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-[0.2em]">New account</span>
          <h1 className="mt-6 text-4xl font-black tracking-tight sm:text-5xl">Create your GoTrip account.</h1>
          <p className="mt-5 text-lg leading-8 text-slate-300">Join a modern booking platform for hotel stays, restaurant reservations, favorites, and secure checkout.</p>
          <div className="mt-10 space-y-4">
            <div className="rounded-3xl bg-white/10 p-5"><p className="font-black">One account</p><p className="mt-2 text-sm text-slate-300">Manage rooms and dining from one dashboard.</p></div>
            <div className="rounded-3xl bg-white/10 p-5"><p className="font-black">Instant access</p><p className="mt-2 text-sm text-slate-300">Book, edit, and track reservations quickly.</p></div>
          </div>
        </div>

        <form onSubmit={form.handleSubmit} className="premium-card bg-white p-6 sm:p-8">
          <h2 className="text-2xl font-black tracking-tight text-ink">Start booking in minutes</h2>
          <p className="mt-3 leading-7 text-muted">Create your account to save favorites and manage reservations.</p>

          <div className="mt-7 grid gap-4 sm:grid-cols-2">
            <label><span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Name</span><input className={fieldClass('name')} name="name" value={form.values.name} onChange={form.handleChange} onBlur={form.handleBlur} /></label>
            <label><span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Family</span><input className={fieldClass('family')} name="family" value={form.values.family} onChange={form.handleChange} onBlur={form.handleBlur} /></label>
            <label><span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Mobile</span><input className={fieldClass('mobile_number')} name="mobile_number" value={form.values.mobile_number} onChange={form.handleChange} onBlur={form.handleBlur} /></label>
            <label><span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Email</span><input className={fieldClass('email')} name="email" type="email" value={form.values.email} onChange={form.handleChange} onBlur={form.handleBlur} /></label>
            <label><span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Password</span><input className={fieldClass('password')} name="password" type="password" value={form.values.password} onChange={form.handleChange} onBlur={form.handleBlur} /></label>
            <label><span className="mb-2 block text-xs font-bold uppercase tracking-wide text-muted">Confirm password</span><input className={fieldClass('repassword')} name="repassword" type="password" value={form.values.repassword} onChange={form.handleChange} onBlur={form.handleBlur} /></label>
          </div>

          <div className="mt-4 space-y-2 text-sm text-red-500">
            {form.errors.name && form.touched.name && <p>{form.errors.name}</p>}
            {form.errors.family && form.touched.family && <p>{form.errors.family}</p>}
            {form.errors.mobile_number && form.touched.mobile_number && <p>{form.errors.mobile_number}</p>}
            {form.errors.email && form.touched.email && <p>{form.errors.email}</p>}
            {form.errors.password && form.touched.password && <p>{form.errors.password}</p>}
            {form.errors.repassword && form.touched.repassword && <p>{form.errors.repassword}</p>}
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Link to="/login" className="text-sm font-bold text-gotrip-700 hover:text-gotrip-800">Already have an account?</Link>
            <button type="submit" disabled={form.isSubmitting} className="btn-primary w-full sm:w-auto">{form.isSubmitting ? 'Loading...' : 'Register'}</button>
          </div>
        </form>
      </div>
    </section>
  );
}
